<script lang="ts" setup>
import Arrow from "@/assets/icons/arrow.svg?component";
import getBlogArticles from "@/graphql/blog-articles.query.graphql";
import type {
  Category,
  BaseImageFragment,
  GetBlogArticlesQuery,
  GetBlogArticlesQueryVariables,
} from "@/types/graphql";

// composables
const { locale, d, t } = useI18n();
const localePath = useLocalePath();
const route = useRoute();
const router = useRouter();
const device = useDevice();

const page = ref(route.query.page ? parseInt(route.query.page as string) : 1);
const pageSizeMobile = 6;
const pageSizeOther = 12;
const pageSize = ref(device.isMobile ? pageSizeMobile : pageSizeOther);

const filtersElement = ref<HTMLElement | null>(null);
// Set component vars
/**
 * Fliter model an share global state
 */
const filterModel = ref<string[]>([]);
// set filter if route params are set
filterModel.value = route.query.filter
  ? Array.isArray(route.query.filter)
    ? route.query.filter
    : [route.query.filter]
  : [];
// build object for graphql filter
const filters = computed(() => {
  if (!filterModel.value.length) return undefined;
  return { category: { name: { in: filterModel.value } } };
});

// set data fetching
/**
 * Fetch all Blog-Artilcles
 * Map the result object to the component props
 */
const { result, refetch } = useQuery<
  GetBlogArticlesQuery,
  GetBlogArticlesQueryVariables
>(getBlogArticles, {
  locale: locale.value,
  filters: filters.value,
  page: page.value,
  pageSize: pageSize.value,
});

const maxPages = computed(() => {
  return result.value?.blogArticles?.meta?.pagination?.pageCount || 1;
});

// parse response for blog articles
const blogArticles = computed(() =>
  result.value?.blogArticles?.data.reduce<
    {
      id: string;
      headline: string;
      subheadline: string;
      url: string;
      image: BaseImageFragment;
      category?: Category | null;
    }[]
  >((validatedArticles, blogArticle) => {
    if (!blogArticle.attributes) return validatedArticles;
    const { id } = blogArticle;
    const { title, createdAt, slug, thumbnail, category } =
      blogArticle.attributes;
    if (title && createdAt && slug && id)
      validatedArticles.push({
        id: id,
        headline: title,
        subheadline: d(blogArticle.attributes?.publishedAt, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }),
        url: route.path + "/" + slug,
        image: thumbnail?.data?.attributes,
        category: category?.data?.attributes,
      });
    return validatedArticles;
  }, []),
);

// parse response for categories
const categories = computed(() =>
  result.value?.categories?.data.map((category) => {
    return {
      description: category.attributes?.description,
      name: category.attributes?.name,
    };
  }),
);

// refecth with filter value
watch(filterModel, async () => {
  page.value = 1;
  router.replace({
    query: {
      ...router.currentRoute.value.query,
      filter: filterModel.value,
      page: page.value,
    },
  });
  refetch({
    filters: filters.value,
    page: page.value,
  });
});

const nextPage = () => {
  if (page.value >= maxPages.value) return;
  page.value++;

  refetch({
    page: page.value,
  });

  router.replace({
    query: {
      ...router.currentRoute.value.query,
      filter: filterModel.value,
      page: page.value,
    },
  });

  if (filtersElement.value) {
    filtersElement.value.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });
  }
};

const previousPage = () => {
  if (page.value <= 1) return;
  page.value--;

  refetch({
    page: page.value,
  });

  router.replace({
    query: {
      ...router.currentRoute.value.query,
      filter: filterModel.value,
      page: page.value,
    },
  });

  if (filtersElement.value) {
    filtersElement.value.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });
  }
};
</script>

<i18n lang="json">
{
  "de": {
    "page": "Seite",
    "nextPage": "Nächste Seite",
    "previousPage": "Vorherige Seite"
  },
  "en": {
    "page": "Page",
    "nextPage": "Next page",
    "previousPage": "Previous page"
  }
}
</i18n>

<template>
  <div
    ref="filtersElement"
    class="blog-list outer-space container scroll-mt-20 pb-12 pt-12"
  >
    <div class="module-filter flex flex-wrap gap-2">
      <base-filter
        v-for="(category, index) in categories"
        :key="`${category.name}-${index}`"
        v-model="filterModel"
        :value="category.name"
        :title="category.description"
      >
        <span>{{ category.name }}</span>
      </base-filter>
    </div>

    <div
      class="blog-list mt-12 grid auto-rows-fr grid-cols-12 gap-5.2 md:gap-5"
    >
      <module-link-card
        v-for="blogArticle in blogArticles"
        :key="blogArticle.id"
        :headline="blogArticle.headline"
        :subheadline="blogArticle.subheadline"
        :url="localePath(blogArticle.url)"
        :image="blogArticle.image"
        :categories="blogArticle.category"
        type="blog"
        class="col-span-12 md:col-span-6 lg:col-span-4"
        imgsizes="12.5rem"
      />
    </div>

    <div class="mt-6 flex items-center justify-center gap-4">
      <base-button
        class="btn btn-primary mt-4 p-3"
        :disabled="page <= 1"
        type="secondary"
        :aria-label="t('previousPage')"
        @click="previousPage()"
      >
        <arrow class="rotate-180 transform" />
      </base-button>
      <p class="text-base mt-4 font-bold">
        {{ t("page") }} {{ page }} / {{ maxPages }}
      </p>
      <base-button
        class="btn btn-primary mt-4 p-3"
        :disabled="page >= maxPages"
        type="secondary"
        :aria-label="t('nextPage')"
        @click="nextPage()"
      >
        <arrow />
      </base-button>
    </div>
  </div>
</template>

<style scoped>
:deep(.link-card-image) {
  @apply max-h-[200px] max-w-[200px];
}
</style>
